import Aurora from '@js/autoload/Aurora'
import { breakpoint } from '@js/mediaQueries'

/**
 * Presents a basic toast with content
 */
export default class Toast {
    static FIRST_CLEAR_TIMING = 3000
    static ALIVE_DURATION = 1000

    static globalRegion = undefined
    static ariaLiveRegion = undefined
    static cleanupTimeout = undefined
    static cleanupInterval = undefined
    static activeToasts = []

    constructor(content, ariaMessage) {
        if (!Toast.globalRegion) {
            Toast.globalRegion = document.createElement('div')
            Toast.globalRegion.classList.add('global-toasts')
            document.body.appendChild(Toast.globalRegion)

            const canvasWrapper = document.createElement('div')
            canvasWrapper.classList.add('aurora-wrapper')

            const canvas = document.createElement('canvas')
            canvas.classList.add('aurora')

            canvasWrapper.appendChild(canvas)
            Toast.globalRegion.appendChild(canvasWrapper)

            new ToastAurora(canvas)

            Toast.ariaLiveRegion = document.createElement('div')
            Toast.ariaLiveRegion.classList.add('sr-only')
            Toast.ariaLiveRegion.ariaLive = 'polite'
            Toast.ariaLiveRegion.ariaAtomic = 'true'
            Toast.ariaLiveRegion.ariaRelevant = 'additions text'
            Toast.globalRegion.appendChild(Toast.ariaLiveRegion)
        }

        this.toastWrapper = document.createElement('div')
        this.toastWrapper.classList.add('toast-wrapper', 'shrunk')

        this.toastCollapse = document.createElement('div')
        this.toastCollapse.classList.add('toast-collapse')
        this.toastWrapper.append(this.toastCollapse)

        this.toast = document.createElement('div')
        this.toast.classList.add('toast')
        this.toast.innerHTML = content
        this.toastCollapse.append(this.toast)

        Toast.globalRegion.append(this.toastWrapper)
        Toast.activeToasts.push(this.toastWrapper)

        window.addEventListener('pagehide', Toast.onPageHide)
        window.addEventListener('pageshow', Toast.setCleanupTimeout)

        document.addEventListener("visibilitychange", function() {
            if (document.hidden) {
                Toast.onPageHide()
            } else {
                Toast.setCleanupTimeout()
            }
        })

        requestAnimationFrame(() => {
            this.toastWrapper.classList.remove('shrunk')

            setTimeout(() => {
                const dummy = document.createElement('div')
                dummy.innerHTML = ariaMessage || content

                let message = dummy.textContent
                if (message === Toast.ariaLiveRegion.textContent) {
                    message = `${message}\u00A0`
                }

                Toast.ariaLiveRegion.innerHTML = message
            }, 150)

            Toast.setCleanupTimeout()
        })
    }

    /**
     * Called during page hide events to clear timeouts and intervals
     */
    static onPageHide = () => {
        clearTimeout(Toast.cleanupTimeout)
        clearInterval(Toast.cleanupInterval)
    }

    /**
     * Starts the cleanup interval
     */
    static setCleanupTimeout = () => {
        // Resets the clear timeout if a new Toast is added to the stack
        clearTimeout(Toast.cleanupTimeout)
        clearInterval(Toast.cleanupInterval)
        if (Toast.activeToasts.length > 0) {
            Toast.cleanupTimeout = setTimeout(() => {
                Toast.cleanupInterval = setInterval(Toast.scheduleClearOldestToast, Toast.ALIVE_DURATION)
            }, Toast.FIRST_CLEAR_TIMING - Toast.ALIVE_DURATION)
        }
    }

    /**
     * Animates and removes the oldest Toast element from the DOM
     */
    static scheduleClearOldestToast = () => {
        if (Toast.activeToasts.length === 0) {
            return
        } else if (Toast.activeToasts.length === 1) {
            clearInterval(Toast.cleanupInterval)
        }

        const toast = Toast.activeToasts.shift()
        toast.addEventListener('transitionend', (event) => {
            if (event.propertyName === 'grid-template-rows') {
                Toast.globalRegion.removeChild(toast)
            }
        })
        toast.classList.add('shrunk')
    }
}

/**
 * Extends Aurora to update the gradient colors
 * @extends Aurora
 */
class ToastAurora extends Aurora {
    get colors() {
        return {
            lightMode: [
                '#2afdcc',
                '#d930d9',
                '#4111c5',
                '#9cd5ff',
                '#ffc368'
            ],
            darkMode: [
                '#015C9d',
                '#066952',
                '#e00094',
                '#8215ee',
                '#c96b13'
            ]
        }
    }

    get gradientFraction() {
        if (['xsmall', 'small'].includes(breakpoint())) {
            return 1 / 10
        }

        return 1 / 8
    }
}
